import { BACKEND_URL } from "@/lib/api";
import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { ArrowUpRight, Eye, Sparkles } from "lucide-react";
import { Badge } from "@/components/ui/badge";

const BACKEND = BACKEND_URL;

export default function CustomerStories({ heading = "Real numbers, real replays.", subheading = "Every card below is computed live from production data — no PDFs, no marketing fluff.", showHeading = true, maxCards = 6 }) {
  const [stories, setStories] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    axios
      .get(`${BACKEND}/api/public/customer-stories`)
      .then((r) => {
        if (cancelled) return;
        setStories((r.data?.stories || []).slice(0, maxCards));
      })
      .catch(() => {})
      .finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
  }, [maxCards]);

  if (loading && stories.length === 0) {
    return (
      <section className="py-20" data-testid="customer-stories-loading">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 text-sm text-slate-500">Loading live stories…</div>
      </section>
    );
  }
  if (stories.length === 0) return null;

  return (
    <section className="py-24 sm:py-28" data-testid="customer-stories">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        {showHeading && (
          <div className="max-w-3xl">
            <Badge variant="outline" className="rounded-full border-slate-300 bg-white text-slate-700 px-3 py-1">
              <Sparkles className="size-3 mr-1.5 text-[#0055FF]" />
              Live customer telemetry
            </Badge>
            <h2 className="mt-4 text-4xl sm:text-5xl tracking-[-0.03em] font-medium text-slate-900">{heading}</h2>
            <p className="mt-4 text-slate-600 text-lg max-w-2xl">{subheading}</p>
          </div>
        )}

        <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {stories.map((s, i) => (
            <StoryCard key={s.sector} story={s} index={i} />
          ))}
        </div>

        <div className="mt-10 flex items-center gap-2 text-xs text-slate-500">
          <span className="inline-flex items-center gap-2">
            <span className="size-1.5 rounded-full bg-[#22D3A6] animate-pulse" />
            Re-computed every page load
          </span>
          <span>·</span>
          <span>All replay transcripts are anonymised — no PII.</span>
        </div>
      </div>
    </section>
  );
}

function StoryCard({ story, index }) {
  return (
    <div
      data-testid={`story-card-${story.sector}`}
      className="group relative rounded-2xl border border-slate-200 bg-white p-6 shadow-soft hover:shadow-float hover:border-[#0055FF]/30 transition-all duration-300"
      style={{ animationDelay: `${index * 60}ms` }}
    >
      <div className="flex items-center justify-between mb-5">
        <div className="text-[10.5px] font-semibold uppercase tracking-[0.18em] text-slate-500">
          {story.sector_name}
        </div>
        <Link
          to={`/sectors/${story.sector}`}
          data-testid={`story-sector-link-${story.sector}`}
          className="text-slate-400 hover:text-[#0055FF] transition-colors"
        >
          <ArrowUpRight className="size-4" />
        </Link>
      </div>

      <div className="space-y-1">
        <div className="text-[42px] font-semibold tracking-[-0.035em] text-slate-900 tabular-nums leading-none">
          {story.leads_30d.toLocaleString()}
        </div>
        <div className="text-xs text-slate-500">leads routed in last 30 days</div>
      </div>

      <div className="mt-5 grid grid-cols-2 gap-3 pt-4 border-t border-slate-100">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-slate-500">Accept rate</div>
          <div className="mt-1 text-lg font-semibold text-[#0055FF] tabular-nums">{story.accept_rate}%</div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-widest text-slate-500">Avg lead value</div>
          <div className="mt-1 text-lg font-semibold text-slate-900 tabular-nums">£{story.avg_value.toFixed(2)}</div>
        </div>
      </div>

      {story.replay_id && (
        <Link
          to={`/replay/${story.replay_id}`}
          data-testid={`story-replay-link-${story.sector}`}
          className="mt-5 flex items-center gap-2.5 rounded-lg border border-slate-200 bg-slate-50/40 p-3 hover:border-[#0055FF]/40 hover:bg-white transition-all"
        >
          <span className="inline-grid place-items-center size-7 rounded-md bg-[#0F172A] text-white shrink-0">
            <Eye className="size-3.5" strokeWidth={1.8} />
          </span>
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-widest text-slate-500">Top AI replay</div>
            <div className="text-xs text-slate-700 truncate font-medium">
              {story.replay_intent ? `"${story.replay_intent}"` : "View transcript"}
            </div>
          </div>
          <span className="ml-auto text-[10px] font-mono text-slate-500 tabular-nums shrink-0">{story.replay_views}×</span>
        </Link>
      )}

      {!story.replay_id && (
        <div className="mt-5 text-[11px] text-slate-500">
          {story.qualified_conversations} qualified AI conversation{story.qualified_conversations === 1 ? "" : "s"} on file
        </div>
      )}
    </div>
  );
}
